import { SpiffToolAdminAction, Task } from '@kalos/kalos-rpc';
import { useCallback } from 'react';

import { useAuth } from '../../../../context/AuthContext';
import {
  useSpiffAdminActionCreateMutation,
  useSpiffAdminActionUpdateMutation,
  useTaskUpdateMutation,
} from '../../../../hooks/react-query/useTaskClientServiceQuery';
import { useUserQuery } from '../../../../hooks/react-query/useUserQuery';
import { timestamp } from '../../../../tools/helpers';
import { sendSpiffDecisionEmail } from './spiffDecisionEmail';
import { type SpiffToolAdminActionFormSchema } from './SpiffToolAdminActionForm';

export const useCreateOrUpdateSpiffToolAdminAction = ({ parentTask }: { parentTask: Task }) => {
  const updateTaskMutation = useTaskUpdateMutation();

  const updateAdminTaskActionMutation = useSpiffAdminActionUpdateMutation();
  const createAdminTaskActionMutation = useSpiffAdminActionCreateMutation();
  const loggedUser = useAuth().user;

  const taskUserQuery = useUserQuery({
    filters: {
      id: parentTask.externalId,
    },
  });

  const createEmail = useCallback(
    async (data: Task, newAction: SpiffToolAdminAction) => {
      if (!taskUserQuery.isSuccess) return;
      await sendSpiffDecisionEmail(data, taskUserQuery.data, newAction);
    },
    [taskUserQuery.data, taskUserQuery.isSuccess],
  );

  type CreateOrUpdateSpiffToolAdminActionArgs = {
    adminAction: SpiffToolAdminActionFormSchema;
  } & ({ type: 'Update'; originalId: SpiffToolAdminAction['id'] } | { type: 'Create' });

  return useCallback(
    async ({ adminAction, type, ...rest }: CreateOrUpdateSpiffToolAdminActionArgs) => {
      const newReviewedBy = loggedUser.firstname + ' ' + loggedUser.lastname;
      const timestampValue = timestamp(true, adminAction.decisionDate);
      const adminActionNew = SpiffToolAdminAction.create({
        id: type === 'Update' && 'originalId' in rest ? rest.originalId : 0,
        taskId: parentTask.id,
        reviewedBy: newReviewedBy,
        decisionDate: timestampValue,
        reason: adminAction.reason,
        status: adminAction.status,
        fieldMask: ['DecisionDate', 'Status', 'Reason'],
      });

      adminActionNew.reviewedBy = newReviewedBy;
      adminActionNew.decisionDate = timestampValue;
      adminActionNew.fieldMask.push('DecisionDate');

      if (adminActionNew.status === 0) {
        adminActionNew.status = 1;
        //because of the safe form object, we are not getting the default status, so in this instance 0===1
      }
      if (adminActionNew.status === 1) {
        adminActionNew.grantedDate = timestampValue;
        console.log('granted');
        adminActionNew.fieldMask.push('GrantedDate');
      }
      if (adminActionNew.status === 3) {
        console.log('revoked');
        adminActionNew.revokedDate = timestampValue;
        adminActionNew.fieldMask.push('RevokedDate');
      }
      if (adminActionNew.reason === null || adminActionNew.reason === '') {
        adminActionNew.reason = 'No Reason Given';
      }
      let createdActionId = 0;
      if (type === 'Create') {
        createdActionId = (await createAdminTaskActionMutation.mutateAsync(adminActionNew)).id;
      } else {
        createdActionId = (await updateAdminTaskActionMutation.mutateAsync(adminActionNew)).id;
      }
      Promise.allSettled([createEmail(parentTask, adminActionNew)]);
      //this was used to update the record, we should replace this with an insert trigger
      const updateTask = Task.create({ fieldMask: ['AdminActionId'] });
      updateTask.id = parentTask.id;
      updateTask.adminActionId = createdActionId;
      updateTask.spiffToolCloseoutDate = timestamp();

      await updateTaskMutation.mutateAsync(updateTask);
    },
    [
      createAdminTaskActionMutation,
      createEmail,
      loggedUser.firstname,
      loggedUser.lastname,
      parentTask,
      updateAdminTaskActionMutation,
      updateTaskMutation,
    ],
  );
};
